import { useEffect, useRef, useState, useCallback } from 'react'
import ReactDOM from 'react-dom'
import { Step } from 'react-joyride'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import { useDebouncedCallback } from 'builder/hooks/useDebouncedCallback'

import { animationStyles, Z_INDEX } from '../utils'
import { Dot, Wave, Wrapper } from './styles'

type Props = {
  step: Step
  disablePulse?: boolean
}

export const Pulse = ({ step, disablePulse }: Props) => {
  const { isPhone, isTablet } = useMediaQueries()
  const divRef = useRef(document.createElement('div'))
  const [showPulse, setShowPulse] = useState(false)
  const [pulsePosition, setPulsePosition] = useState({ top: 0, left: 0 })
  const el = document.querySelector(step.target as string) as HTMLElement | null

  const handleClick = useCallback(() => {
    step.data?.onClick?.()
  }, [step.data])

  const getPosition = () => {
    if (!el) return { top: 0, left: 0 }
    const rect = el.getBoundingClientRect()
    const PULSE_SIZE = disablePulse ? 6 : 27

    const { top, left, width, height } = rect
    const centerX = left + width / 2 - PULSE_SIZE
    const centerY = top + height / 2 - PULSE_SIZE

    if (step.placement?.includes('bottom')) {
      return { top: top + height - PULSE_SIZE, left: centerX }
    }
    if (step.placement?.includes('right')) {
      return { top: centerY, left: left + width - PULSE_SIZE }
    }
    if (step.placement?.includes('left')) {
      return { top: centerY, left: left - PULSE_SIZE }
    }
    return { top: top - PULSE_SIZE, left: centerX }
  }

  const onScrollPositionChange = useDebouncedCallback(() => {
    setPulsePosition(getPosition())
  }, 20)

  const setStyles = () => {
    if ((isPhone || isTablet) && step.target === '#drag-handle-card') {
      el?.style.setProperty('z-index', `${Z_INDEX + 100}`)
    }
  }

  const cleanStyles = () => {
    if ((isPhone || isTablet) && step.target === '#drag-handle-card') {
      el?.style.removeProperty('z-index')
    }
  }

  useEffect(() => {
    if (el) {
      setStyles()
      el.addEventListener('click', handleClick)
      el.addEventListener('scroll', onScrollPositionChange)
      window.addEventListener('scroll', onScrollPositionChange)
      setShowPulse(true)
      setPulsePosition(getPosition())
    }

    return () => {
      cleanStyles()
      el?.removeEventListener('click', handleClick)
      el?.removeEventListener('scroll', onScrollPositionChange)
      window.removeEventListener('scroll', onScrollPositionChange)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [el])

  useEffect(() => {
    const div = divRef.current
    document.body.appendChild(div)
    return () => div.remove()
  }, [])

  if (!showPulse || !el) return null

  return ReactDOM.createPortal(
    <Wrapper
      disablePulse={disablePulse}
      onClick={() => el.click()}
      style={{
        ...{ ...pulsePosition, left: disablePulse ? pulsePosition.left + 7 : pulsePosition.left },
        ...animationStyles,
      }}
    >
      {!disablePulse ? (
        <>
          <Dot />
          <Wave delay="0s" />
          <Wave delay="0.8s" />
          <Wave delay="1.6s" />
        </>
      ) : (
        <Dot />
      )}
    </Wrapper>,
    divRef.current,
  )
}
